import { ROUTE_THANK_YOU } from '../constants';
import {
  type BrazeLoyaltyLoginMethod,
  buildLoginPayload,
  type LoginPayloadSource,
} from '../helpers/brazeHelper';
import { trackImmediate } from './pulseTracker';
import type { PulseEventPartial } from './pulseSchema';
import { PULSE_VIEW_ID } from './pulseViewIds';

export type PulseLoginMethod = BrazeLoyaltyLoginMethod | 'Facebook';

export type TrackLoginContext = {
  isCheckoutLogin?: boolean;
  isSignupPage?: boolean;
  isAutoLogin?: boolean;
};

export const resolveLoginSource = ({
  isCheckoutLogin = false,
  isSignupPage = false,
  isAutoLogin = false,
}: TrackLoginContext): LoginPayloadSource => {
  if (isAutoLogin) return 'auto_after_order';
  if (isCheckoutLogin) return 'guest_checkout_modal';
  if (isSignupPage) return 'signup_page';
  return 'login_page';
};

export const resolveLoginViewId = (loginSource: LoginPayloadSource): string => {
  switch (loginSource) {
    case 'login_page':
      return PULSE_VIEW_ID.LOGIN;
    case 'signup_page':
      return PULSE_VIEW_ID.SIGNUP;
    case 'guest_checkout_modal':
      return PULSE_VIEW_ID.GUEST_LOGIN_MODAL;
    case 'auto_after_order':
      return PULSE_VIEW_ID.THANK_YOU;
    default:
      return PULSE_VIEW_ID.LOGIN;
  }
};

export const mapSocialSignInToLoginMethod = (
  signInName: string
): PulseLoginMethod => {
  switch (signInName) {
    case 'GOOGLE_SIGNIN':
      return 'Gmail';
    case 'APPLE_SIGNIN':
      return 'Apple';
    case 'FACEBOOK_SIGNIN':
      return 'Facebook';
    default:
      return 'Email';
  }
};

export const buildLoginPulsePartial = (params: {
  userData: any;
  isItMobile: boolean;
  loginMethod: PulseLoginMethod;
  page_path?: string;
} & TrackLoginContext): PulseEventPartial => {
  const loginSource = resolveLoginSource(params);
  const loginPayload = buildLoginPayload(
    params.userData,
    params.isItMobile,
    params.loginMethod,
    loginSource
  );
  const path =
    params.page_path ??
    (loginSource === 'auto_after_order'
      ? ROUTE_THANK_YOU
      : typeof window !== 'undefined'
        ? window.location.pathname
        : null);

  return {
    event_name: 'login',
    view_id: resolveLoginViewId(loginSource),
    page_path: path ?? undefined,
    user_id: params.userData?.id ?? loginPayload.CzId ?? null,
    email: params.userData?.email ?? loginPayload.email ?? null,
    action: 'login',
  };
};

/** Pulse `login` — immediate send. */
export const trackLogin = (
  params: {
    userData: any;
    isItMobile: boolean;
    loginMethod: PulseLoginMethod;
    page_path?: string;
  } & TrackLoginContext
): void => {
  if (!params.userData?.id) return;
  trackImmediate(buildLoginPulsePartial(params));
};

export const isMobileWebPlatform = (platform?: string | null): boolean =>
  platform === 'MobileWeb';
